/**
 * Explicit Strapi populate maps per endpoint.
 * Prefer these over `populate: 'deep'` so each page only loads what it renders.
 */

/** One extra relation/media level under a populated entry. */
const withRelations = { populate: '*' as const }

/** A Lead Form relation with its fields and their dropdown choices. */
const leadFormPopulate = {
  populate: {
    fields: {
      populate: {
        optionList: { populate: { options: true } },
      },
    },
  },
} as const

const leadFormBlockPopulate = {
  populate: { lead_form: leadFormPopulate },
} as const

/** Dynamic-zone blocks shared by homepage and category pages. */
const homepageCmsBlockPopulate = {
  on: {
    'headers.category-page-header': {
      populate: {
        image: true,
      },
    },
    'cards.product-cards': {
      populate: {
        product_cards: {
          populate: {
            logo: true,
            productImage: true,
            button: true,
          },
        },
      },
    },
    'separators.text-separator': true,
    'separators.image-separator': {
      populate: {
        image: true,
      },
    },
    'separators.button-separator': {
      populate: {
        separator: withRelations,
      },
    },
    'scrollers.product-scroller': {
      populate: {
        horizontal_scroller: {
          populate: {
            product_cards: {
              populate: {
                logo: true,
                productImage: true,
                button: true,
              },
            },
          },
        },
      },
    },
    'button.button': true,
    'forms.lead-form-block': leadFormBlockPopulate,
    // `seo.seo` intentionally omitted — page title comes from attributes.seo
  },
} as const

/** Product technology-page dynamic-zone blocks (HXD-169 / HXD-174).
 * Zone components are relation wrappers around existing collection types —
 * populate the relation, then the nested media/components on that entry.
 */
const productCmsBlockPopulate = {
  on: {
    'products.landing': {
      populate: {
        technology_header: {
          populate: {
            backgroundImage: true,
            mobileBackgroundImage: true,
            logo: true,
            productImage: true,
            buttons: true,
            chip: withRelations,
          },
        },
      },
    },
    'products.introducing': {
      populate: {
        introduction: {
          populate: {
            cards: {
              populate: {
                image: true,
              },
            },
          },
        },
      },
    },
    'products.demo-video': {
      populate: {
        demo_video: {
          populate: {
            placeholder: true,
            video: true,
          },
        },
      },
    },
    'products.picture-info-panel': {
      populate: {
        picture_info_panel: {
          populate: {
            images: true,
            button: true,
            listItems: {
              populate: {
                icon: true,
              },
            },
          },
        },
      },
    },
    'products.before-and-after': {
      populate: {
        before_and_after: {
          populate: {
            // Repeatable component → each item has its own `images` media field
            images: {
              populate: {
                images: true,
              },
            },
          },
        },
      },
    },
    'products.separator': {
      populate: {
        separator: {
          populate: {
            button: true,
          },
        },
      },
    },
    'products.image-section': {
      populate: {
        image_section: {
          populate: {
            image: true,
          },
        },
      },
    },
    'products.treatment-areas': {
      populate: {
        treatment_area: {
          populate: {
            items: {
              populate: {
                image: true,
              },
            },
          },
        },
      },
    },
    'products.testimonials': {
      populate: {
        testimonial: {
          populate: {
            testimonialItems: true,
          },
        },
      },
    },
    'products.features-list-panel': {
      populate: {
        features_list_panel: {
          populate: {
            listItemsLeft: {
              populate: {
                icon: true,
              },
            },
            listItemsRight: {
              populate: {
                icon: true,
              },
            },
          },
        },
      },
    },
    'products.product-variant-panel': {
      populate: {
        productVariantPanel: {
          populate: {
            images: true,
          },
        },
      },
    },
    'products.faq': {
      populate: {
        faq: {
          populate: {
            faqItems: true,
          },
        },
      },
    },
    'forms.lead-form-block': leadFormBlockPopulate,
  },
} as const

export const footerPopulate = {
  socialMedia: {
    populate: {
      image: true,
    },
  },
  links: true,
  companyLogo: true,
} as const

/** Nav/search and contact form only need slug + name. */
/** Campaign pages (slug product/campaign) are left out of menus and product lists. */
export const technologyPagesListParams = {
  fields: ['slug', 'technologyName'] as const,
  filters: { slug: { $notContains: '/' } },
} as const

export const homepagePopulate = {
  seo: true,
  homepage_carousels: {
    populate: {
      button: true,
      logo: true,
      chip: withRelations,
      backgroundImage: true,
      mobileBackgroundImage: true,
    },
  },
  components: homepageCmsBlockPopulate,
} as const

export const categoryPagePopulate = {
  seo: true,
  components: homepageCmsBlockPopulate,
} as const

export const technologyPagePopulate = {
  seo: true,
  leadForm: leadFormPopulate,
  components: productCmsBlockPopulate,
} as const

export const contactUsPopulate = {
  seo: true,
  separator: true,
} as const

export const formSettingsPopulate = {
  productEnquiry: leadFormPopulate,
  contactUs: leadFormPopulate,
  contactPpc: leadFormPopulate,
  contactPs: leadFormPopulate,
} as const

export const legalPagePopulate = {
  seo: true,
} as const
